// ─────────────────────────────────────────────────────────────────────────────
// BizPlus parity — role permission matrix  (Phase 0.2, wiring)
//
// The screen that makes the 199-key catalogue operable. Pick a role, tick the
// actions it may perform, save. Keys that differ from the catalogue default are
// marked, so an administrator can always see what has been deliberately changed
// versus what is simply inherited.
//
// Actions differ per module (a master has no "approve", attendance has no
// "convert to invoice"), so this renders ragged rows rather than a fixed column
// grid — a grid would be mostly empty cells and imply keys that do not exist.
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo, useState, useEffect } from 'react';
import { Shield, Search, RotateCcw, Check, Lock } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { ROLE_LABELS } from '@/shared/permissions/roles';
import toast from 'react-hot-toast';
import { Spinner } from '@/shared/components/ui/Spinner';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { cn } from '@/shared/utils/utils';
import {
  usePermissionCatalog,
  useRolePermissions,
  useSaveRolePermissions,
} from '../hooks/usePermissionAdmin';

// Labels come from the single canonical map in shared/permissions/roles.ts.

const DEPARTMENT_FILTERS = [
  { key: 'all', label: 'All Modules', icon: '🌐' },
  { key: 'sales', label: 'Sales & CRM', icon: '🎯' },
  { key: 'accounts', label: 'Accounts & Finance', icon: '💼' },
  { key: 'manufacturing', label: 'Manufacturing & Inventory', icon: '🏭' },
  { key: 'hr', label: 'HR & Payroll', icon: '👥' },
  { key: 'operations', label: 'Operations & IT', icon: '🛠️' },
  { key: 'admin', label: 'Administration & Governance', icon: '🛡️' },
];

const MODULE_DEPARTMENT_CATEGORY: Record<string, string> = {
  // Sales & CRM
  leads: 'sales',
  deals: 'sales',
  enquiry: 'sales',
  quotation: 'sales',
  proforma: 'sales',
  invoice: 'sales',
  sales: 'sales',
  challan: 'sales',
  dispatch: 'sales',
  sales_return: 'sales',
  sampling: 'sales',
  clients: 'sales',
  contacts: 'sales',
  visits: 'sales',
  campaigns: 'sales',
  pricing: 'sales',
  complaints: 'sales',
  targets: 'sales',
  lead_inbox: 'sales',
  scraper: 'sales',
  call_center: 'sales',
  vouchers: 'sales',
  loyalty: 'sales',

  // Accounts & Finance
  accounting: 'accounts',
  coa: 'accounts',
  journals: 'accounts',
  accounting_vouchers: 'accounts',
  vouchers_acc: 'accounts',
  pdc: 'accounts',
  reconcile: 'accounts',
  taxes: 'accounts',
  eway_bill: 'accounts',
  expenses: 'accounts',
  cashbook: 'accounts',
  reports: 'accounts',
  audit_logs: 'accounts',
  invoice_edit_requests: 'accounts',
  field_expense: 'accounts',

  // Manufacturing & Inventory
  manufacturing: 'manufacturing',
  inventory: 'manufacturing',
  purchase_order: 'manufacturing',
  purchase: 'manufacturing',
  purchase_return: 'manufacturing',
  products: 'manufacturing',
  stock_transfer: 'manufacturing',
  pos: 'manufacturing',
  fleet: 'manufacturing',

  // HR & Payroll
  hr: 'hr',
  attendance: 'hr',
  payroll: 'hr',
  recruitment: 'hr',
  employee: 'hr',
  leaves: 'hr',
  biometrics: 'hr',
  departments: 'hr',

  // Operations & IT
  projects: 'operations',
  workflows: 'operations',
  tasks: 'operations',
  service_desk: 'operations',
  communication: 'operations',
  ai: 'operations',
  notepad: 'operations',
  mailbox: 'operations',
  integrations: 'operations',
  templates: 'operations',

  // Administration & Governance
  masters: 'admin',
  settings: 'admin',
  administration: 'admin',
  trash: 'admin',
  oversight: 'admin',
  command_center: 'admin',
  portal_accounts: 'admin',
  admin_tools: 'admin',
};

const DEPT_BADGE_COLORS: Record<string, string> = {
  sales: 'bg-info/10 text-info-text border-info/25',
  accounts: 'bg-success/10 text-success-text border-success/25',
  manufacturing: 'bg-warning/10 text-warning-text border-warning/25',
  hr: 'bg-purple-50 text-purple-700 border-purple-200',
  operations: 'bg-info/10 text-info-text border-info/25',
  admin: 'bg-danger/10 text-danger-text border-danger/25',
};

export function RoleMatrixPage() {
  const catalog = usePermissionCatalog();
  const [role, setRole] = useState<string | null>(null);
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<Set<string> | null>(null);

  const roleQuery = useRolePermissions(role);
  const save = useSaveRolePermissions();

  // Default to the first editable role once the catalogue arrives.
  useEffect(() => {
    if (!role && catalog.data?.roles?.length) {
      setRole(catalog.data.roles.find((r) => r !== 'superadmin') ?? catalog.data.roles[0]);
    }
  }, [catalog.data, role]);

  // Reset the draft whenever a different role's data loads, so switching roles
  // never carries unsaved ticks across to another role.
  useEffect(() => {
    if (roleQuery.data) setDraft(new Set(roleQuery.data.effective));
  }, [roleQuery.data]);

  const defaults = useMemo(
    () => new Set(roleQuery.data?.defaults ?? []),
    [roleQuery.data],
  );

  const modules = useMemo(() => {
    const all = catalog.data?.modules ?? [];
    const term = search.trim().toLowerCase();
    
    return all
      .filter((m) => {
        if (departmentFilter === 'all') return true;
        const dept = MODULE_DEPARTMENT_CATEGORY[m.module] || 'operations';
        return dept === departmentFilter;
      })
      .map((m) => ({
        ...m,
        deptCategory: MODULE_DEPARTMENT_CATEGORY[m.module] || 'operations',
        actions: m.actions.filter(
          (a) =>
            !term ||
            m.label.toLowerCase().includes(term) ||
            m.module.includes(term) ||
            a.label.toLowerCase().includes(term) ||
            a.key.includes(term),
        ),
      }))
      .filter((m) => m.actions.length > 0);
  }, [catalog.data, search, departmentFilter]);

  const locked = roleQuery.data?.locked ?? false;
  const dirty = useMemo(() => {
    if (!draft || !roleQuery.data) return false;
    const current = new Set(roleQuery.data.effective);
    if (current.size !== draft.size) return true;
    for (const k of draft) if (!current.has(k)) return true;
    return false;
  }, [draft, roleQuery.data]);

  const changedCount = useMemo(() => {
    if (!draft) return 0;
    let n = 0;
    for (const key of draft) if (!defaults.has(key)) n += 1;
    for (const key of defaults) if (!draft.has(key)) n += 1;
    return n;
  }, [draft, defaults]);

  const toggle = (key: string) => {
    if (locked) return;
    setDraft((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleModule = (keys: string[], on: boolean) => {
    if (locked) return;
    setDraft((prev) => {
      const next = new Set(prev ?? []);
      for (const k of keys) {
        if (on) next.add(k);
        else next.delete(k);
      }
      return next;
    });
  };

  const handleSave = () => {
    if (!role || !draft) return;
    const roleLabel = ROLE_LABELS[role] || role;
    save.mutate(
      { role, keys: [...draft] },
      {
        onSuccess: () => {
          toast.success(`Permissions updated successfully for ${roleLabel}`);
        },
        onError: (err: any) => {
          toast.error(err?.response?.data?.error || `Failed to save permissions for ${roleLabel}`);
        },
      }
    );
  };

  if (catalog.isLoading) {
    return <div className="flex items-center justify-center py-20"><Spinner /></div>;
  }
  if (catalog.isError) {
    return (
      <div className="p-6">
        <EmptyState
          title="Could not load the permission catalogue"
          description="You may not have permission to view this screen, or the server is unreachable."
        />
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-16">
      <PageHeader
        icon={Shield}
        eyebrow="Administration"
        title="Roles & Permissions"
        description={<>
          {catalog.data?.totalKeys} actions across {catalog.data?.modules.length} modules.
          Ticking an action changes what the role may do; who they may do it to is
          decided separately by record ownership.
        </>}
        actions={<div className="flex items-center gap-2">
          {dirty && !locked && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDraft(new Set(roleQuery.data?.effective ?? []))}
            >
              <RotateCcw className="mr-1 h-4 w-4" aria-hidden />
              Discard
            </Button>
          )}
          <Button
            size="sm"
            disabled={!dirty || locked}
            loading={save.isPending}
            onClick={handleSave}
className="font-semibold"
          >
            <Check className="mr-1.5 h-4 w-4" aria-hidden />
            Save changes
          </Button>
        </div>}
      />

      {/* Role selector */}
      <div role="tablist" aria-label="Role" className="flex flex-wrap gap-2">
        {(catalog.data?.roles ?? []).map((r) => {
          const isSelected = r === role;
          return (
            <button
              key={r}
              role="tab"
              aria-selected={isSelected}
              onClick={() => setRole(r)}
              className={cn(
                'rounded-lg border px-3.5 py-1.5 text-body-sm font-medium transition-colors',
                isSelected
                  ? 'border-primary/40 bg-primary/10 text-primary'
                  : 'border-outline bg-surface text-on-surface-variant hover:border-primary/40 hover:text-on-surface',
              )}
            >
              {ROLE_LABELS[r] ?? r}
            </button>
          );
        })}
      </div>

      {/* Department Category Filter */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
            Filter by Department Scope:
          </span>
          {departmentFilter !== 'all' && !locked && draft && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const deptKeys = modules.flatMap((m) => m.actions.map((a) => a.key));
                  setDraft((prev) => {
                    const next = new Set(prev ?? []);
                    for (const k of deptKeys) next.add(k);
                    return next;
                  });
                }}
                className="text-xs font-bold text-info-text hover:text-info-text bg-info/10 px-2.5 py-1 rounded-lg border border-info/15 transition-colors"
              >
                Grant All in {DEPARTMENT_FILTERS.find((f) => f.key === departmentFilter)?.label}
              </button>
              <button
                type="button"
                onClick={() => {
                  const deptKeys = modules.flatMap((m) => m.actions.map((a) => a.key));
                  setDraft((prev) => {
                    const next = new Set(prev ?? []);
                    for (const k of deptKeys) next.delete(k);
                    return next;
                  });
                }}
                className="text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200 transition-colors"
              >
                Revoke All in {DEPARTMENT_FILTERS.find((f) => f.key === departmentFilter)?.label}
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {DEPARTMENT_FILTERS.map((f) => {
            const isSelected = departmentFilter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setDepartmentFilter(f.key)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-body-sm font-medium transition-colors border',
                  isSelected
                    ? 'bg-primary/10 text-primary border-primary/40'
                    : 'bg-surface text-on-surface-variant border-outline hover:border-primary/40',
                )}
              >
                <span>{f.icon}</span>
                <span>{f.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {locked && (
        <div className="flex items-start gap-2.5 rounded-xl border border-warning/25 bg-warning/10 px-4 py-3 text-warning-text">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-warning-text" aria-hidden />
          <p className="text-xs font-medium">
            The Owner role always holds every permission and cannot be edited. If it
            could be narrowed, an administrator could revoke access to this very
            screen and leave nobody able to restore it.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Input
          leftIcon={<Search className="h-4 w-4 text-slate-400" />}
          placeholder="Filter modules and actions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm rounded-xl border-slate-200 shadow-sm"
        />
        {changedCount > 0 && (
          <span className="text-xs font-bold text-info-text bg-info/10 px-3 py-1.5 rounded-full border border-info/15">
            {changedCount} permission{changedCount === 1 ? '' : 's'} customized from default
          </span>
        )}
      </div>

      {roleQuery.isLoading || !draft ? (
        <div className="flex items-center justify-center py-16"><Spinner /></div>
      ) : modules.length === 0 ? (
        <EmptyState title="Nothing matches that filter" description="Try a different module or action name." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm divide-y divide-slate-100">
          {modules.map((m, i) => {
            const keys = m.actions.map((a) => a.key);
            const activeCount = keys.filter((k) => draft.has(k)).length;
            const allOn = activeCount === keys.length;
            const deptColor = DEPT_BADGE_COLORS[m.deptCategory] || 'bg-slate-50 text-slate-700 border-slate-200';

            return (
              <div
                key={m.module}
                className={cn(
                  'flex flex-col gap-2.5 px-5 py-3.5 sm:flex-row sm:items-center sm:gap-6 transition-colors',
                  i % 2 === 1 ? 'bg-slate-50/50' : 'bg-white',
                )}
              >
                <div className="flex w-full items-center justify-between gap-2 sm:w-64 sm:shrink-0">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-bold text-slate-900">{m.label}</span>
                      <span className={cn('text-[10px] font-bold px-1.5 py-0.5 rounded border uppercase', deptColor)}>
                        {m.deptCategory}
                      </span>
                    </div>
                    <span className="text-[11px] font-medium text-slate-400">
                      {activeCount}/{keys.length} allowed
                    </span>
                  </div>
                  {!locked && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => toggleModule(keys, true)}
                        className={cn(
                          "px-2 py-0.5 text-[11px] font-bold rounded-md transition-colors",
                          allOn ? "bg-info/10 text-info-text" : "text-slate-500 hover:bg-slate-100"
                        )}
                        title="Grant all in this module"
                      >
                        all
                      </button>
                      <span className="text-slate-300 text-[10px]">|</span>
                      <button
                        type="button"
                        onClick={() => toggleModule(keys, false)}
                        className={cn(
                          "px-2 py-0.5 text-[11px] font-bold rounded-md transition-colors",
                          activeCount === 0 ? "bg-slate-200 text-slate-700" : "text-slate-500 hover:bg-slate-100"
                        )}
                        title="Revoke all in this module"
                      >
                        none
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 items-center flex-1">
                  {m.actions.map((a) => {
                    const on = draft.has(a.key);
                    const isDefault = defaults.has(a.key);
                    const changed = on !== isDefault;
                    return (
                      <button
                        key={a.key}
                        type="button"
                        title={changed ? `${a.key} (Modified from default)` : a.key}
                        disabled={locked}
                        onClick={() => toggle(a.key)}
                        aria-pressed={on}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-body-sm font-medium transition-colors',
                          on
                            ? 'bg-primary/10 text-primary border border-primary/30 hover:bg-primary/15'
                            : 'bg-surface text-on-surface-subtle border border-outline hover:border-primary/40 hover:text-on-surface',
                          changed && 'ring-2 ring-warning/40 ring-offset-1',
                          locked ? 'cursor-not-allowed opacity-60' : 'active:scale-95',
                        )}
                      >
                        {on && <Check className="w-3.5 h-3.5 stroke-[2.5]" />}
                        {a.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
